import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, Target, TrendingUp, Calendar, DollarSign, AlertCircle, CheckCircle, Clock, Save, X, Home, GraduationCap, Plane, Heart, Briefcase, Brain, Sparkles, Crown } from 'lucide-react';
import { collection, addDoc, deleteDoc, doc, getDocs, updateDoc, query } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../AuthContext';
import { useCountry } from '../CountryContext';
import { formatCurrency } from '../countryConfig';
import {
  calculateGoalProgress,
  calculateMonthsRemaining,
  calculateRequiredMonthlySavings,
  projectFutureValue,
  getGoalStatus,
  sortGoalsByPriority,
  getGoalTypes,
  validateGoal,
  formatMonthsDuration
} from '../utils/goalCalculations';

const GOAL_TYPE_ICONS = {
  'Emergency Fund': AlertCircle,
  'Debt Payoff': TrendingUp,
  'Home Purchase': Home,
  'Education': GraduationCap,
  'Retirement': Clock,
  'Investment': DollarSign,
  'Vacation': Plane,
  'Wedding': Heart,
  'Car Purchase': Target,
  'Business': Briefcase,
  'Other': Target
};

const FinancialGoals = ({ isDark, currentNetWorth = 0, userProfile, onUpgradeClick }) => {
  const { currentUser } = useAuth();
  const { selectedCountry } = useCountry();
  const [goals, setGoals] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sortBy, setSortBy] = useState('priority'); // priority, date, progress

  const [formData, setFormData] = useState({
    name: '',
    type: 'Other',
    targetAmount: '',
    currentAmount: '',
    targetDate: '',
    monthlyContribution: '',
    monthlyExpenses: '', // For Emergency Fund goals - to calculate months coverage
    expectedReturn: '5',
    notes: ''
  });

  // Load goals from Firestore
  useEffect(() => {
    if (currentUser) {
      loadGoals();
    }
  }, [currentUser]);

  const loadGoals = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, 'users', currentUser.uid, 'financialGoals')
      );
      const querySnapshot = await getDocs(q);
      const loadedGoals = [];

      querySnapshot.forEach((doc) => {
        loadedGoals.push({
          id: doc.id,
          ...doc.data()
        });
      });

      setGoals(loadedGoals);
    } catch (error) {
      console.error('Error loading goals:', error);
      alert('Failed to load goals. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'Other',
      targetAmount: '',
      currentAmount: '',
      targetDate: '',
      monthlyContribution: '',
      monthlyExpenses: '',
      expectedReturn: '5',
      notes: ''
    });
  };

  const handleAddGoal = async (e) => {
    e.preventDefault();

    const goalData = {
      name: formData.name,
      type: formData.type,
      targetAmount: parseFloat(formData.targetAmount) || 0,
      currentAmount: parseFloat(formData.currentAmount) || 0,
      targetDate: formData.targetDate,
      monthlyContribution: parseFloat(formData.monthlyContribution) || 0,
      monthlyExpenses: parseFloat(formData.monthlyExpenses) || 0, // For Emergency Fund
      expectedReturn: parseFloat(formData.expectedReturn) || 0,
      notes: formData.notes
    };

    const validation = validateGoal(goalData);
    if (!validation.isValid) {
      alert(validation.errors.join('\n'));
      return;
    }

    setSaving(true);
    try {
      goalData.createdAt = new Date().toISOString();
      goalData.updatedAt = new Date().toISOString();

      await addDoc(
        collection(db, 'users', currentUser.uid, 'financialGoals'),
        goalData
      );

      await loadGoals();
      setShowAddModal(false);
      resetForm();
    } catch (error) {
      console.error('Error adding goal:', error);
      alert('Failed to add goal. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleEditGoal = async (e) => {
    e.preventDefault();

    const goalData = {
      name: formData.name,
      type: formData.type,
      targetAmount: parseFloat(formData.targetAmount) || 0,
      currentAmount: parseFloat(formData.currentAmount) || 0,
      targetDate: formData.targetDate,
      monthlyContribution: parseFloat(formData.monthlyContribution) || 0,
      monthlyExpenses: parseFloat(formData.monthlyExpenses) || 0, // For Emergency Fund
      expectedReturn: parseFloat(formData.expectedReturn) || 0,
      notes: formData.notes
    };

    const validation = validateGoal(goalData);
    if (!validation.isValid) {
      alert(validation.errors.join('\n'));
      return;
    }

    setSaving(true);
    try {
      goalData.updatedAt = new Date().toISOString();

      await updateDoc(
        doc(db, 'users', currentUser.uid, 'financialGoals', editingGoal.id),
        goalData
      );

      await loadGoals();
      setShowEditModal(false);
      setEditingGoal(null);
      resetForm();
    } catch (error) {
      console.error('Error updating goal:', error);
      alert('Failed to update goal. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!window.confirm('Are you sure you want to delete this goal?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'users', currentUser.uid, 'financialGoals', goalId));
      await loadGoals();
    } catch (error) {
      console.error('Error deleting goal:', error);
      alert('Failed to delete goal. Please try again.');
    }
  };

  const openEditModal = (goal) => {
    setEditingGoal(goal);
    setFormData({
      name: goal.name,
      type: goal.type,
      targetAmount: goal.targetAmount.toString(),
      currentAmount: goal.currentAmount?.toString() || '0',
      targetDate: goal.targetDate,
      monthlyContribution: goal.monthlyContribution?.toString() || '0',
      monthlyExpenses: goal.monthlyExpenses?.toString() || '0', // For Emergency Fund
      expectedReturn: goal.expectedReturn?.toString() || '5',
      notes: goal.notes || ''
    });
    setShowEditModal(true);
  };

  // Sort goals
  const sortedGoals = React.useMemo(() => {
    let sorted = [...goals];

    if (sortBy === 'priority') {
      sorted = sortGoalsByPriority(goals);
    } else if (sortBy === 'date') {
      sorted.sort((a, b) => new Date(a.targetDate) - new Date(b.targetDate));
    } else if (sortBy === 'progress') {
      sorted.sort((a, b) => {
        const progressA = calculateGoalProgress(a.currentAmount, a.targetAmount);
        const progressB = calculateGoalProgress(b.currentAmount, b.targetAmount);
        return progressB - progressA;
      });
    }

    return sorted;
  }, [goals, sortBy]);

  // Calculate summary stats
  const totalTargetAmount = goals.reduce((sum, g) => sum + g.targetAmount, 0);
  const totalCurrentAmount = goals.reduce((sum, g) => sum + (g.currentAmount || 0), 0);
  const totalMonthlyContribution = goals.reduce((sum, g) => sum + (g.monthlyContribution || 0), 0);
  const completedGoals = goals.filter(g => {
    const progress = calculateGoalProgress(g.currentAmount, g.targetAmount);
    return progress >= 100;
  }).length;

  const getGoalIcon = (type) => {
    return GOAL_TYPE_ICONS[type] || Target;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className={`p-6 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Active Goals
              </p>
              <p className="text-2xl font-bold mt-1">
                {goals.length}
              </p>
            </div>
            <Target className="w-12 h-12 text-blue-500 opacity-20" />
          </div>
        </div>

        <div className={`p-6 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Total Target
              </p>
              <p className="text-2xl font-bold text-blue-500 mt-1">
                {formatCurrency(totalTargetAmount, selectedCountry)}
              </p>
            </div>
            <DollarSign className="w-12 h-12 text-blue-500 opacity-20" />
          </div>
        </div>

        <div className={`p-6 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Total Saved
              </p>
              <p className="text-2xl font-bold text-green-500 mt-1">
                {formatCurrency(totalCurrentAmount, selectedCountry)}
              </p>
            </div>
            <TrendingUp className="w-12 h-12 text-green-500 opacity-20" />
          </div>
        </div>

        <div className={`p-6 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Completed
              </p>
              <p className="text-2xl font-bold text-green-500 mt-1">
                {completedGoals}
              </p>
            </div>
            <CheckCircle className="w-12 h-12 text-green-500 opacity-20" />
          </div>
        </div>
      </div>

      {/* AI Insights Teaser for Non-Premium Users */}
      {userProfile?.plan !== 'premium' && goals.length > 0 && (
        <div className={`relative overflow-hidden rounded-xl border-2 ${
          isDark
            ? 'bg-gradient-to-br from-blue-900/40 to-purple-900/40 border-purple-500/50'
            : 'bg-gradient-to-br from-blue-50 to-purple-50 border-purple-300'
        } p-4 sm:p-6`}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center flex-shrink-0 ${
              isDark ? 'bg-blue-500/20' : 'bg-blue-100'
            }`}>
              <Brain className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className={`text-base sm:text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Want AI-Powered Goal Insights?
                </h3>
                <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" />
              </div>
              <p className={`text-xs sm:text-sm mb-3 sm:mb-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Upgrade to Premium and let AI analyze your goals to tell you:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                <div className="flex items-start gap-2">
                  <Sparkles className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                  <span className={`text-xs sm:text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Which goals to prioritize first
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <Sparkles className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                  <span className={`text-xs sm:text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Optimal investment strategy by timeline
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <Sparkles className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                  <span className={`text-xs sm:text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Realistic feasibility assessment
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <Sparkles className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                  <span className={`text-xs sm:text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Asset allocation recommendations
                  </span>
                </div>
              </div>
              <button
                onClick={onUpgradeClick}
                className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-semibold transition-all shadow-md text-sm flex items-center justify-center gap-2"
              >
                <Crown className="w-4 h-4" />
                Upgrade to Premium - Try Free for 14 Days
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header with controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-2xl font-bold">Your Financial Goals</h2>
        <div className="flex gap-3 items-center">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className={`px-4 py-2 rounded-lg border ${
              isDark
                ? 'bg-gray-800 border-gray-700 text-white'
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          >
            <option value="priority">Sort by Priority</option>
            <option value="date">Sort by Date</option>
            <option value="progress">Sort by Progress</option>
          </select>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Goal
          </button>
        </div>
      </div>

      {/* Goals List */}
      {sortedGoals.length === 0 ? (
        <div className={`text-center py-12 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <Target className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            No financial goals yet
          </p>
          <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'} mt-2`}>
            Set your first goal and start tracking your progress
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {sortedGoals.map((goal) => {
            const Icon = getGoalIcon(goal.type);
            const status = getGoalStatus(goal);
            const requiredMonthlySavings = calculateRequiredMonthlySavings({
              currentAmount: goal.currentAmount || 0,
              targetAmount: goal.targetAmount,
              targetDate: goal.targetDate,
              expectedReturn: goal.expectedReturn || 0
            });

            const projectedAmount = projectFutureValue({
              currentAmount: goal.currentAmount || 0,
              monthlyContribution: goal.monthlyContribution || 0,
              months: status.monthsRemaining || 0,
              expectedReturn: goal.expectedReturn || 0
            });

            const willReachGoal = projectedAmount >= goal.targetAmount;

            return (
              <div
                key={goal.id}
                className={`p-6 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg hover:shadow-xl transition-shadow border-l-4`}
                style={{ borderLeftColor: status.color }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div
                      className="p-3 rounded-lg"
                      style={{ backgroundColor: `${status.color}20` }}
                    >
                      <Icon className="w-6 h-6" style={{ color: status.color }} />
                    </div>

                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-lg font-semibold">{goal.name}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-sm px-2 py-1 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                              {goal.type}
                            </span>
                            <span
                              className="text-sm px-2 py-1 rounded text-white font-semibold"
                              style={{ backgroundColor: status.color }}
                            >
                              {status.statusText}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="mt-4">
                        <div className="flex justify-between text-sm mb-1">
                          <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                            Progress
                          </span>
                          <span className="font-semibold">
                            {formatCurrency(goal.currentAmount || 0, selectedCountry)} / {formatCurrency(goal.targetAmount, selectedCountry)}
                          </span>
                        </div>
                        <div className={`w-full h-3 rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                          <div
                            className="h-3 rounded-full transition-all"
                            style={{
                              width: `${Math.min(status.progress, 100)}%`,
                              backgroundColor: status.color
                            }}
                          />
                        </div>
                        <div className="flex justify-between text-sm mt-1">
                          <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                            {status.progress.toFixed(1)}% complete
                          </span>
                          {status.monthsRemaining !== null && (
                            <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                              {formatMonthsDuration(status.monthsRemaining)} remaining
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Stats Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                        {goal.monthlyContribution > 0 && (
                          <div>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                              Monthly Saving
                            </p>
                            <p className="text-sm font-semibold">
                              {formatCurrency(goal.monthlyContribution, selectedCountry)}
                            </p>
                          </div>
                        )}

                        {requiredMonthlySavings !== null && (
                          <div>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                              Required Monthly
                            </p>
                            <p className={`text-sm font-semibold ${
                              goal.monthlyContribution >= requiredMonthlySavings ? 'text-green-500' : 'text-red-500'
                            }`}>
                              {formatCurrency(requiredMonthlySavings, selectedCountry)}
                            </p>
                          </div>
                        )}

                        {goal.targetDate && (
                          <div>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                              Target Date
                            </p>
                            <p className="text-sm font-semibold">
                              {new Date(goal.targetDate).toLocaleDateString()}
                            </p>
                          </div>
                        )}

                        {projectedAmount > 0 && status.monthsRemaining > 0 && (
                          <div>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                              Projected Amount
                            </p>
                            <p className={`text-sm font-semibold ${willReachGoal ? 'text-green-500' : 'text-orange-500'}`}>
                              {formatCurrency(projectedAmount, selectedCountry)}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Warning messages */}
                      {!willReachGoal && status.monthsRemaining > 0 && goal.monthlyContribution > 0 && (
                        <div className="mt-3 p-3 bg-orange-500 bg-opacity-10 border border-orange-500 rounded-lg">
                          <p className="text-sm text-orange-500">
                            ⚠️ At current savings rate, you'll have {formatCurrency(projectedAmount, selectedCountry)} by target date.
                            Consider increasing monthly contribution to {formatCurrency(requiredMonthlySavings, selectedCountry)}.
                          </p>
                        </div>
                      )}

                      {goal.notes && (
                        <p className={`text-sm mt-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {goal.notes}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => openEditModal(goal)}
                      className="p-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteGoal(goal.id)}
                      className="p-2 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className={`max-w-2xl w-full rounded-lg p-6 ${isDark ? 'bg-gray-800' : 'bg-white'} max-h-[90vh] overflow-y-auto`}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">
                {showEditModal ? 'Edit Goal' : 'Add New Goal'}
              </h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                  setEditingGoal(null);
                  resetForm();
                }}
                className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={showEditModal ? handleEditGoal : handleAddGoal} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Goal Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Down Payment on House"
                  className={`w-full p-3 rounded-lg border ${
                    isDark
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Goal Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className={`w-full p-3 rounded-lg border ${
                    isDark
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  required
                >
                  {getGoalTypes().map(type => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Target Amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="targetAmount"
                    value={formData.targetAmount}
                    onChange={handleInputChange}
                    placeholder="50000"
                    step="0.01"
                    min="0"
                    className={`w-full p-3 rounded-lg border ${
                      isDark
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Current Amount
                  </label>
                  <input
                    type="number"
                    name="currentAmount"
                    value={formData.currentAmount}
                    onChange={handleInputChange}
                    placeholder="5000"
                    step="0.01"
                    min="0"
                    className={`w-full p-3 rounded-lg border ${
                      isDark
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Target Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="targetDate"
                    value={formData.targetDate}
                    onChange={handleInputChange}
                    min={new Date().toISOString().split('T')[0]}
                    className={`w-full p-3 rounded-lg border ${
                      isDark
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Monthly Contribution
                  </label>
                  <input
                    type="number"
                    name="monthlyContribution"
                    value={formData.monthlyContribution}
                    onChange={handleInputChange}
                    placeholder="500"
                    step="0.01"
                    min="0"
                    className={`w-full p-3 rounded-lg border ${
                      isDark
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                </div>
              </div>

              {/* Conditional Monthly Expenses field for Emergency Fund goals */}
              {formData.type === 'Emergency Fund' && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-700 rounded-lg p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                        Emergency Fund Calculation
                      </h4>
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        Enter your monthly expenses to see how many months of coverage you have.
                        This helps us provide accurate emergency fund recommendations.
                      </p>
                    </div>
                  </div>
                  <label className="block text-sm font-medium mb-2">
                    Monthly Expenses <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="monthlyExpenses"
                    value={formData.monthlyExpenses}
                    onChange={handleInputChange}
                    placeholder="2000"
                    step="0.01"
                    min="0"
                    className={`w-full p-3 rounded-lg border ${
                      isDark
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    required={formData.type === 'Emergency Fund'}
                  />
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                    Include all regular monthly expenses: rent/mortgage, utilities, food, transportation, insurance, etc.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">
                  Expected Annual Return (%)
                </label>
                <input
                  type="number"
                  name="expectedReturn"
                  value={formData.expectedReturn}
                  onChange={handleInputChange}
                  placeholder="5"
                  step="0.1"
                  min="0"
                  max="100"
                  className={`w-full p-3 rounded-lg border ${
                    isDark
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
                <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Expected investment return if you're investing the savings (0 for cash savings)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Notes
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Additional notes about this goal..."
                  rows="3"
                  className={`w-full p-3 rounded-lg border ${
                    isDark
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-5 h-5" />
                      {showEditModal ? 'Update Goal' : 'Add Goal'}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    setEditingGoal(null);
                    resetForm();
                  }}
                  className={`px-6 py-3 rounded-lg ${
                    isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
                  } transition-colors`}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialGoals;
